import { Component, EventEmitter, Input, Output, ViewChild } from '@angular/core';

import { Observable, forkJoin, catchError, of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';

import { SourceChip } from '../../models/source-chip.model';
import { DropdownOptionModel } from '../../../Forms/models/dropdown-option.model';
import { SourceService } from '../../services/source.service';
import { ActionKeys } from '../../../Forms/enums/keys.enum';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { TextInputComponent } from '../../../Forms/components/TextInput/text-input.component';
import { Source } from '../../models/source.model';
import { SourceTypeEnum } from '../../../../api/source/enums/source-type.enum';

@Component({
    selector: 'agpr-create-source-input',
    styleUrls: ['./create-source-input.component.scss'],
    templateUrl: './create-source-input.component.html',
    standalone: false
})
export class CreateSourceInputComponent {
    @Input() id: string;
    @Input() label: string = 'Sources.sourcePicker.create';

    @Output() sourcesAdded: EventEmitter<number[]> = new EventEmitter<number[]>();
    @Output() sourceRemoved: EventEmitter<number> = new EventEmitter<number>();

    @ViewChild('textInput') textInput: TextInputComponent;

    sourceChips: SourceChip[] = [];

    sourceTypeOptions: DropdownOptionModel<SourceTypeEnum>[] = Object.values(SourceTypeEnum)
        .filter(type => type !== SourceTypeEnum.sourceGroup)
        .map(type => {
            return {
                label: `Common.journodex.${type}`,
                value: type
            };
        });
    selectedType: DropdownOptionModel<SourceTypeEnum> = this.sourceTypeOptions[0];

    newSourceName: string;
    sourceErrors: string[] = [];

    private addingSources: boolean = false;

    readonly splitOnPattern = new RegExp('[,;]');

    constructor(
        public sourceService: SourceService,
        public localizationService: LocalizationService
    ) {}


    removeSource(chip: SourceChip) {
        this.sourceChips = this.sourceChips.filter(source => source !== chip);
        this.sourceRemoved.emit(chip.id);
    }


    validateSources(event?: KeyboardEvent) {
        this.sourceErrors = [];

        if (
            (event && event?.key !== ActionKeys.ENTER) ||
            !this.newSourceName ||
            this.newSourceName?.trim().length === 0
        ) {
            return;
        }

        const dupeRequests: Observable<boolean>[] = [];

        const names = this.newSourceName
            .split(this.splitOnPattern)
            .filter(name => name.length);

        names.forEach(name => {
            const cleanedName = name.trim();

            dupeRequests.push(
                this.sourceService.isNameDuplicated$(cleanedName).pipe(
                    tap((isDuplicate) => {
                        if (isDuplicate) {
                            this.sourceErrors.push(
                                this.localizationService.transform('Sources.error.duplicate.name', {name: cleanedName})
                            );
                        }
                    })
                )
            );
        });

        forkJoin(dupeRequests).subscribe(x => {
            if (this.sourceErrors.length === 0 && !this.addingSources) {
                this.addingSources = true;
                this.addNewSources(names);
            }
        });
    }

    private addNewSources(names: string[]) {
        const requests: Observable<number>[] = [];
        const ids: number[] = [];

        names.forEach(name => {
            const source = new Source();
            source.type = this.selectedType.value;
            source.name = name.trim();

            requests.push(
                this.sourceService.create(source).pipe(
                    catchError(() => of(null)),
                    tap((id) => {
                        if (id) {
                            source.id = id;
                            ids.push(id);
                            this.sourceChips.push(source.toChipModel());
                        }
                    })
                )
            );
        });

        forkJoin(requests).subscribe({
            next: () => {
                this.textInput.clearInput();
                this.newSourceName = '';
                this.addingSources = false;
                this.sourcesAdded.emit(ids);
            },
            error: () => {
                this.addingSources = false;
            }
        });
    }
}
